import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, GraduationCap, Plus } from 'lucide-react';

import {
  createCourse,
  listCourses,
  type AdminCourseListRow,
  type CourseState,
} from '@/api/trainingAdmin.api';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { describeTrainingError } from '@/lib/training/errors';
import { useToast } from '@/hooks/useToast';
import { TrainingNoAccessCard } from './_chrome';
import { useTrainingAdminGate } from './_gate';

/**
 * Course authoring index (manage_training / superadmin). Lists every
 * course in every state — drafts and archived included — with a
 * create-course modal; rows open the builder.
 */

const STATE_META: Record<CourseState, { label: string; variant: BadgeProps['variant'] }> = {
  draft: { label: 'Draft', variant: 'info' },
  published: { label: 'Published', variant: 'success' },
  archived: { label: 'Archived', variant: 'neutral' },
};

export function CourseListPage() {
  const { canManage } = useTrainingAdminGate();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const coursesQuery = useQuery({
    queryKey: ['training-admin', 'courses'],
    queryFn: listCourses,
    enabled: canManage,
  });

  if (!canManage) {
    return <TrainingNoAccessCard capability="manage_training" />;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Training courses</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Author lessons and quizzes, then publish to make a course assignable.
          </p>
        </div>
        <Button
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          <Plus className="size-4" />
          New course
        </Button>
      </div>

      {coursesQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : coursesQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <p className="text-[13px] text-kw-danger-text">
            {describeTrainingError(coursesQuery.error, 'Could not load courses.')}
          </p>
        </Card>
      ) : (coursesQuery.data ?? []).length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <GraduationCap className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No courses yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Create your first course, add lessons and a quiz, then publish it.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="min-w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
                <th className="px-4 py-2.5 font-medium">Title</th>
                <th className="px-4 py-2.5 font-medium">State</th>
                <th className="px-4 py-2.5 font-medium">Version</th>
                <th className="px-4 py-2.5 font-medium">Lessons</th>
                <th className="px-4 py-2.5 font-medium">Quizzes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kw-border">
              {(coursesQuery.data ?? []).map((course: AdminCourseListRow) => {
                const meta = STATE_META[course.state];
                return (
                  <tr
                    key={course.id}
                    className="cursor-pointer hover:bg-kw-bg"
                    onClick={() => {
                      void navigate(`/admin/training/courses/${course.id}`);
                    }}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-kw-shark">{course.title}</p>
                      {course.category ? (
                        <p className="mt-0.5 text-[11px] text-kw-muted">{course.category}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-kw-shark">v{course.version}</td>
                    <td className="px-4 py-3 text-kw-shark">{course.lessonCount}</td>
                    <td className="px-4 py-3 text-kw-shark">{course.quizCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {createOpen ? (
        <CreateCourseModal
          onClose={() => {
            setCreateOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateCourseModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [passThreshold, setPassThreshold] = useState(80);

  const mutation = useMutation({
    mutationFn: () =>
      createCourse({
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        category: category.trim() ? category.trim() : null,
        passThresholdPercentDefault: passThreshold,
      }),
    onSuccess: (course) => {
      void queryClient.invalidateQueries({ queryKey: ['training-admin', 'courses'] });
      toast.show('Course created — add lessons, then publish.', { variant: 'success' });
      void navigate(`/admin/training/courses/${course.id}`);
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not create the course.'), { variant: 'danger' });
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!title.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title="New course" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title"
          name="course-title"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="e.g. GMP Fundamentals"
        />
        <Input
          label="Description (optional)"
          name="course-description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Category (optional)"
            name="course-category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
            }}
            placeholder="e.g. Safety"
          />
          <Input
            label="Default pass threshold (%)"
            name="course-pass-threshold"
            type="number"
            min={0}
            max={100}
            required
            value={passThreshold}
            onChange={(e) => {
              setPassThreshold(Number(e.target.value));
            }}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!title.trim()}>
            Create course
          </Button>
        </div>
      </form>
    </Modal>
  );
}
